import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { PayCodesService } from './services/pay-codes.service';
import {
  PayCode,
  PayCodeListFilters,
  PayCodePayload,
  PayCodeType,
} from './models/pay-codes.model';
import { PayCodesResponse } from './models/pay-codes-response.model';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

interface PayCodeFiltersVm {
  searchTerm: string;
}

type ConfirmAction = 'create' | 'update' | 'delete' | 'activate' | 'deactivate';

interface ConfirmState {
  open: boolean;
  action: ConfirmAction | null;
  title: string;
  message: string;
  confirmText: string;
  payCode: PayCode | null;
  form?: NgForm;
}

@Component({
  selector: 'app-pay-codes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppConfigTopNavComponent,
    RouterModule,
    SearchBarComponent,
  ],
  templateUrl: './pay-codes.component.html',
  styleUrls: ['./pay-codes.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PayCodesComponent implements OnInit, OnDestroy {
  payCodes$: Observable<PayCode[]> = of([]);
  payCodeTypes: PayCodeType[] = [];

  isLoading = false;
  isSaving = false;
  errorMessage: string | null = null;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 500];
  totalPages = 1;

  filters: PayCodeFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentPayCode: PayCode | null = null;
  formModel: Partial<PayCode> = this.getEmptyPayCode();

  confirmState: ConfirmState = this.getEmptyConfirmState();

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly payCodesService: PayCodesService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadPayCodeTypes();
    this.loadPayCodes();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  loadPayCodes(): void {
    this.isLoading = true;
    this.errorMessage = null;

    const filters: PayCodeListFilters = {
      searchTerm: this.filters.searchTerm,
    };

    this.payCodes$ = this.payCodesService
      .getPayCodes(this.currentPage, this.pageSize, 'name', filters)
      .pipe(
        map((res: PayCodesResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            return [];
          }

          this.totalPages = res.totalPages ?? 1;
          return res.data ?? [];
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load pay codes';
          this.totalPages = 1;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
      );
  }

  loadPayCodeTypes(): void {
    this.payCodesService.getPayCodeTypes().subscribe({
      next: (types) => {
        this.payCodeTypes = types;
        this.cd.detectChanges();
      },
      error: () => {
        this.payCodeTypes = [];
        this.cd.detectChanges();
      },
    });
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.loadPayCodes();
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadPayCodes();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadPayCodes();
    }
  }

  changePageSize(): void {
    this.currentPage = 1;
    this.loadPayCodes();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentPayCode = null;
    this.formModel = this.getEmptyPayCode();
    this.modalOpen = true;
    this.lockBodyScroll();
  }

  openEditModal(payCode: PayCode): void {
    this.isEditMode = true;
    this.currentPayCode = payCode;
    this.formModel = {
      id: payCode.id,
      name: payCode.name,
      description: payCode.description ?? '',
      type: payCode.type,
      isDayOff: !!payCode.isDayOff,
      isPaid: !!payCode.isPaid,
      isHoliday: !!payCode.isHoliday,
      isActive: !!payCode.isActive,
      shift: payCode.shift ?? null,
    };

    this.modalOpen = true;
    this.lockBodyScroll();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentPayCode = null;
    this.formModel = this.getEmptyPayCode();

    if (!this.confirmState.open) {
      this.unlockBodyScroll();
    }
  }

  requestSubmitPayCode(form?: NgForm): void {
    if (!this.isFormValid()) {
      return;
    }

    this.confirmState = {
      open: true,
      action: this.isEditMode ? 'update' : 'create',
      title: this.isEditMode ? 'Update Pay Code' : 'Create Pay Code',
      message: this.isEditMode
        ? `Are you sure you want to update "${this.formModel.name}"?`
        : `Are you sure you want to create "${this.formModel.name}"?`,
      confirmText: this.isEditMode ? 'Update' : 'Create',
      payCode: this.currentPayCode,
      form,
    };

    this.lockBodyScroll();
  }

  requestDelete(payCode: PayCode): void {
    this.confirmState = {
      open: true,
      action: 'delete',
      title: 'Delete Pay Code',
      message: `Are you sure you want to delete "${payCode.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      payCode,
    };

    this.lockBodyScroll();
  }

  requestActivate(payCode: PayCode): void {
    this.confirmState = {
      open: true,
      action: 'activate',
      title: 'Activate Pay Code',
      message: `Are you sure you want to activate "${payCode.name}"?`,
      confirmText: 'Activate',
      payCode,
    };

    this.lockBodyScroll();
  }

  requestDeactivate(payCode: PayCode): void {
    this.confirmState = {
      open: true,
      action: 'deactivate',
      title: 'Deactivate Pay Code',
      message: `Are you sure you want to deactivate "${payCode.name}"?`,
      confirmText: 'Deactivate',
      payCode,
    };

    this.lockBodyScroll();
  }

  cancelConfirm(): void {
    this.confirmState = this.getEmptyConfirmState();

    if (!this.modalOpen) {
      this.unlockBodyScroll();
    }
  }

  confirmAction(): void {
    if (!this.confirmState.action || this.isSaving) {
      return;
    }

    switch (this.confirmState.action) {
      case 'create':
        this.createPayCode();
        break;
      case 'update':
        this.updatePayCode();
        break;
      case 'delete':
        this.deletePayCode();
        break;
      case 'activate':
        this.activatePayCode();
        break;
      case 'deactivate':
        this.deactivatePayCode();
        break;
    }
  }

  private createPayCode(): void {
    const payload = this.buildPayload();

    this.isSaving = true;

    this.payCodesService
      .createPayCode(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.showToast('Failed to create pay code', 'error');
            return;
          }

          this.cancelConfirm();
          this.closeModal();
          this.showToast('Pay code created successfully', 'success');
          this.loadPayCodes();
        },
        error: () => {
          this.showToast('Failed to create pay code', 'error');
        },
      });
  }

  private updatePayCode(): void {
    if (!this.currentPayCode) {
      return;
    }

    const payload = this.buildPayload();

    this.isSaving = true;

    this.payCodesService
      .updatePayCode(this.currentPayCode.id, payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.showToast('Failed to update pay code', 'error');
            return;
          }

          this.cancelConfirm();
          this.closeModal();
          this.showToast('Pay code updated successfully', 'success');
          this.loadPayCodes();
        },
        error: () => {
          this.showToast('Failed to update pay code', 'error');
        },
      });
  }

  private deletePayCode(): void {
    const payCode = this.confirmState.payCode;

    if (!payCode) {
      return;
    }

    this.isSaving = true;

    this.payCodesService
      .deletePayCode(payCode.id)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.showToast('Failed to delete pay code', 'error');
            return;
          }

          this.cancelConfirm();
          this.showToast('Pay code deleted successfully', 'success');
          this.loadPayCodes();
        },
        error: () => {
          this.showToast('Failed to delete pay code', 'error');
        },
      });
  }

  private activatePayCode(): void {
    const payCode = this.confirmState.payCode;

    if (!payCode) {
      return;
    }

    this.isSaving = true;

    this.payCodesService
      .activatePayCode(payCode.id)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.showToast('Failed to activate pay code', 'error');
            return;
          }

          this.cancelConfirm();
          this.showToast('Pay code activated successfully', 'success');
          this.loadPayCodes();
        },
        error: () => {
          this.showToast('Failed to activate pay code', 'error');
        },
      });
  }

  private deactivatePayCode(): void {
    const payCode = this.confirmState.payCode;

    if (!payCode) {
      return;
    }

    this.isSaving = true;

    this.payCodesService
      .deactivatePayCode(payCode.id)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (success) => {
          if (!success) {
            this.showToast('Failed to deactivate pay code', 'error');
            return;
          }

          this.cancelConfirm();
          this.showToast('Pay code deactivated successfully', 'success');
          this.loadPayCodes();
        },
        error: () => {
          this.showToast('Failed to deactivate pay code', 'error');
        },
      });
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.detectChanges();
    }, 2500);
  }

  private isFormValid(): boolean {
    const name = String(this.formModel.name ?? '').trim();
    const type = String(this.formModel.type ?? '').trim();

    if (!name) {
      this.showToast('Name is required', 'error');
      return false;
    }

    if (!type) {
      this.showToast('Type is required', 'error');
      return false;
    }

    return true;
  }

  private buildPayload(): PayCodePayload {
    return {
      name: String(this.formModel.name ?? '').trim(),
      description: String(this.formModel.description ?? '').trim(),
      type: this.formModel.type as PayCodeType,
      isDayOff: !!this.formModel.isDayOff,
      isPaid: !!this.formModel.isPaid,
      isHoliday: !!this.formModel.isHoliday,
    };
  }

  private getEmptyPayCode(): Partial<PayCode> {
    return {
      name: '',
      description: '',
      type: 'Reg',
      isDayOff: false,
      isPaid: false,
      isHoliday: false,
      isActive: true,
      shift: null,
    };
  }

  private getEmptyConfirmState(): ConfirmState {
    return {
      open: false,
      action: null,
      title: '',
      message: '',
      confirmText: '',
      payCode: null,
    };
  }
}